import type { FC, ReactNode } from "react"
import React, { useId, useRef } from "react"

import { pipe } from "fp-ts/function"
import round from "lodash/round"
import type { FlattenSimpleInterpolation } from "styled-components"
import { css } from "styled-components"

import { Wrapper } from "../utils"

const clampInto =
  ([min, max]: [number, number]) =>
  (value: number): number =>
    value < min ? min : value > max ? max : value

export const defaultNumberInputStyles = css`
  padding-bottom: 15px;
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 12px;
  flex-shrink: 1;
  flex-wrap: wrap;
  width: 100%;
  overflow: hidden;
  &:focus-within {
    label {
      color: var(--blue);
    }
  }
  input {
    display: block;
    transition: all 100ms ease-out;
    flex-shrink: 1;
    font-size: var(--step--2);
    line-height: 19px;
    height: 40px;
    max-width: 60px;
    padding: 6px 6px;
    text-align: left;
    border: 1px solid #c4c4c4;
    border-radius: 5px;
    background-color: white;
    &:disabled {
      background-color: #f5f5f5;
      border-color: #ddd;
      padding: 6px;
      cursor: not-allowed;
    }
  }
  label {
    width: fit-content;
    display: block;
    text-transform: capitalize;
    font-size: var(--step--2);
    white-space: nowrap;
    color: var(--queen-blue);
    font-weight: 600;
  }
  &[data-is-error="true"] {
    input {
      border-color: var(--red-text);
    }
    label {
      color: var(--red-text);
    }
  }
`

export const NumberInputWrapper: FC<{ children: ReactNode }> = ({
  children,
}) => <Wrapper css={defaultNumberInputStyles}>{children}</Wrapper>

export const VALID_NON_NUMBERS = [``, `-`, `.`, `-.`] as const
export type ValidNonNumber = (typeof VALID_NON_NUMBERS)[number]
export const isValidNonNumber = (input: string): input is ValidNonNumber =>
  VALID_NON_NUMBERS.includes(input as ValidNonNumber)
export const VALID_NON_NUMBER_INTERPRETATIONS: Readonly<
  Record<ValidNonNumber, number | null>
> = {
  "": null,
  "-": 0,
  ".": 0,
  "-.": 0,
} as const

export type DecimalInProgress = `${number | ``}.${number | ``}`
export const isDecimalInProgress = (input: string): input is DecimalInProgress =>
  input === `0` || (!isNaN(Number(input)) && input.includes(`.`))

const textToValue = (input: string, allowDecimal: boolean): number | null => {
  if (isValidNonNumber(input)) return VALID_NON_NUMBER_INTERPRETATIONS[input]

  return allowDecimal ? parseFloat(input) : Math.round(parseFloat(input))
}

export type NumberConstraints = {
  max: number
  min: number
  decimalPlaces: number
  nullable: boolean
}
export const DEFAULT_NUMBER_CONSTRAINTS: NumberConstraints = {
  max: Infinity,
  min: -Infinity,
  decimalPlaces: 100,
  nullable: true,
}

const initRefinery =
  <Constraints extends NumberConstraints>(constraints: Partial<Constraints>) =>
  (
    input: number | null,
  ): Constraints extends { nullable: true | undefined }
    ? number | null
    : number => {
    if (input === null && constraints.nullable === true) {
      return null as Constraints extends { nullable: true }
        ? number | null
        : number
    }
    const { max, min, decimalPlaces } = {
      ...DEFAULT_NUMBER_CONSTRAINTS,
      ...constraints,
    }
    const constrained = pipe(input ?? 0, clampInto([min, max]), (n) =>
      round(n, decimalPlaces),
    )

    return constrained
  }

const valueToText = (
  numericValue: number | null | undefined,
  decimalPlacesDisplayed: number | undefined,
  userChangeInProgress: boolean,
): string => {
  if (numericValue === null || numericValue === undefined) {
    return ``
  }

  //if the user is typing, don't round it until they're done
  if (userChangeInProgress) {
    return numericValue.toString()
  }

  return decimalPlacesDisplayed
    ? numericValue.toFixed(decimalPlacesDisplayed)
    : numericValue.toString()
}

type NumberInputProps = Partial<NumberConstraints> & {
  isError?: boolean
  css?: FlattenSimpleInterpolation
  disabled?: boolean
  id?: string
  testId?: string
  label?: string
  name?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onValueCommit?: (numericValue: number | null) => void
  placeholder?: string
  setValue: (numericValue: number | null) => void
  value?: number | null
  defaultValue?: string
  decimalPlacesDisplayed?: number
}

export const NumberInput: FC<NumberInputProps> = ({
  css = defaultNumberInputStyles,
  disabled = false,
  isError = false,
  label,
  max,
  min,
  decimalPlaces = 100,
  decimalPlacesDisplayed,
  name,
  onChange,
  onClick,
  onBlur,
  onValueCommit,
  placeholder = ``,
  setValue,
  value,
  testId,
  defaultValue,
}) => {
  const id = useId()
  const temporaryEntry = useRef<DecimalInProgress | ValidNonNumber | null>(null)
  const userHasMadeDeliberateChange = useRef<boolean>(false)

  const refine = initRefinery({ max, min, decimalPlaces, nullable: true })

  const allowDecimal = decimalPlaces > 0

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(event)
    if (userHasMadeDeliberateChange.current) {
      temporaryEntry.current = null
      const refinedValue = refine(value ?? null)
      setValue(refinedValue)
      onValueCommit?.(refinedValue)
    }
    userHasMadeDeliberateChange.current = false
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event)
    if (setValue === undefined) return
    userHasMadeDeliberateChange.current = true
    const input = event.target.value
    if (isValidNonNumber(input) || isDecimalInProgress(input)) {
      temporaryEntry.current = input
      const textInterpretation = isDecimalInProgress(input)
        ? input
        : (min?.toString() ?? `0`)
      const newValue = textToValue(textInterpretation, allowDecimal)
      setValue(refine(newValue))

      return
    }
    temporaryEntry.current = null
    const inputIsNumeric =
      (!isNaN(Number(input)) && !input.includes(` `)) ||
      (allowDecimal && input === `.`) ||
      (allowDecimal && input === `-.`) ||
      input === `` ||
      input === `-`
    const numericValue = textToValue(input, allowDecimal)

    if (inputIsNumeric) {
      setValue(refine(numericValue))
    }
  }

  const displayValue =
    temporaryEntry.current ??
    valueToText(
      value ? refine(value) : value,
      decimalPlacesDisplayed,
      userHasMadeDeliberateChange.current,
    )

  const showDefaultValue =
    defaultValue && displayValue === `` && !userHasMadeDeliberateChange.current

  return (
    <Wrapper css={css} data-is-error={isError.toString()}>
      {label && <label htmlFor={id}>{label}</label>}
      <input
        type="text"
        value={showDefaultValue ? defaultValue : displayValue}
        placeholder={placeholder ?? `-`}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        name={name ?? id}
        id={id}
        onClick={onClick}
        data-testid={`number-input-${testId ?? id}`}
      />
    </Wrapper>
  )
}
